import { useState } from 'react'
import {
  useAddClientMember,
  useClientMembers,
  useRemoveClientMember,
  useUpdateClientMemberRole,
  type ClientRole,
} from '@/hooks/useAdminActions'
import { AdminError } from './AdminError'

const ROLES: ClientRole[] = ['owner', 'editor', 'viewer']

export function ClientMembers({ clientId }: { clientId: string }) {
  const { data: members, isLoading, error } = useClientMembers(clientId)
  const add = useAddClientMember()
  const updateRole = useUpdateClientMemberRole()
  const remove = useRemoveClientMember()

  const [identifier, setIdentifier] = useState('')
  const [role, setRole] = useState<ClientRole>('viewer')

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!identifier.trim() || add.isPending) return
    add.mutate({ clientId, identifier, role }, { onSuccess: () => setIdentifier('') })
  }

  return (
    <div className="admin-section">
      <h2 className="admin-section-title">Members</h2>

      <form className="admin-inline-form" onSubmit={submit}>
        <input
          className="admin-input"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          placeholder="Email or user id"
        />
        <select className="admin-select" value={role} onChange={(e) => setRole(e.target.value as ClientRole)}>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button className="btn-primary btn--amber" type="submit" disabled={!identifier.trim() || add.isPending}>
          {add.isPending ? 'Adding…' : 'Add'}
        </button>
      </form>
      <p className="admin-field__hint">
        The user must already have signed up. This links an existing account, it does not invite one.
      </p>

      <AdminError error={error ?? add.error ?? updateRole.error ?? remove.error} />

      {isLoading ? (
        <p className="admin-empty-hint">Loading members…</p>
      ) : !members?.length ? (
        <p className="admin-empty-hint">No members assigned.</p>
      ) : (
        <table className="admin-briefs-table">
          <thead>
            <tr><th>User</th><th>Role</th><th /></tr>
          </thead>
          <tbody>
            {members.map(({ membership, user }) => (
              <tr key={membership.id}>
                <td>
                  <div className="admin-member">
                    <span>{user?.full_name || user?.email || 'Unknown user'}</span>
                    <span className="admin-member__id mono">{user?.email ?? membership.user_id}</span>
                  </div>
                </td>
                <td>
                  <select
                    className="admin-select admin-select--sm"
                    value={membership.role}
                    onChange={(e) => updateRole.mutate({ id: membership.id, role: e.target.value as ClientRole, clientId })}
                    disabled={updateRole.isPending}
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td>
                  <button
                    className="admin-link-btn admin-link-btn--danger"
                    onClick={() => remove.mutate({ id: membership.id, clientId })}
                    disabled={remove.isPending}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
